import { PhonicsLesson } from '@/api/types';

export const makeSoundsStringForLesson = (
  lesson: PhonicsLesson,
  delimiter = '',
) =>
  !lesson.sounds
    ? ''
    : `${delimiter}${lesson.sounds.map((sound) => sound.name).join(delimiter)}${delimiter}`;

const lessonConfigOverride = [
  {
    number: 20,
    disableSounds: ['ou'],
  },
  {
    number: 36,
    disableSounds: ['ou'],
  },
];

export const findSoundsInString = (
  sounds: string[],
  inputString: string,
  lessonNumber?: number,
): string[] => {
  const disabledSounds =
    lessonConfigOverride.find((lesson) => lesson.number === lessonNumber)
      ?.disableSounds || [];

  const sortedSounds = [...sounds]
    .filter((sound) => !disabledSounds.includes(sound))
    .sort((a, b) => b.length - a.length);

  const modString = inputString.toLowerCase();

  const foundSounds: { sound: string; index: number }[] = [];

  sortedSounds.forEach((sound) => {
    let index = modString.indexOf(sound);
    while (index !== -1) {
      foundSounds.push({ sound, index });
      index = modString.indexOf(sound, index + 1);
    }
  });

  const segmentation = segmentUsingStrategy(foundSounds);

  return segmentation;
};

const segmentUsingStrategy = (
  foundSounds: { sound: string; index: number }[],
): string[] => {
  foundSounds.sort(
    (a, b) => a.index - b.index || b.sound.length - a.sound.length, // prefer longer sound if same start index
  );

  const finalSounds: string[] = [];
  let lastIndex = -1;

  foundSounds.forEach(({ sound, index }) => {
    if (index >= lastIndex) {
      finalSounds.push(sound);
      lastIndex = index + sound.length;
    }
  });

  return finalSounds;
};

const graphemePhonemeOverrideMap: Record<string, Record<string, string[]>> = {
  a: {
    a_01: ['was'],
  },
  e: {
    e_01: [],
    e_02: ['be', 'he', 'me', 'she', 'we'],
    e_03: ['the'],
  },
  i: {
    i_01: ['i'],
  },
  o: {
    o_01: [],
    o_02: ['do', 'to'],
  },
  oo: {
    oo_01: [
      'book',
      'cook',
      'cooked',
      'cooks',
      'crook',
      'foot',
      'football',
      'good',
      'hood',
      'hoof',
      'hook',
      'look',
      'looked',
      'rook',
      'soot',
      'stood',
      'sisterhood',
      'took',
      'wood',
      'wool',
    ],
    oo_02: [
      'boo',
      'boom',
      'boon',
      'boot',
      'brood',
      'broom',
      'coo',
      'cool',
      'coot',
      'food',
      'fool',
      'hoop',
      'loop',
      'loose',
      'loot',
      'moo',
      'mood',
      'moon',
      'noon',
      'pool',
      'proof',
      'roof',
      'rooftop',
      'room',
      'root',
      'school',
      'scoot',
      'scooting',
      'shoo',
      'shoot',
      'soon',
      'spool',
      'spoon',
      'stool',
      'to',
      'too',
      'tool',
      'toot',
      'tooth',
      'zoo',
      'zoom',
    ],
  },
  th: {
    th_01: [
      'bath',
      'bathtub',
      'beth',
      'birth',
      'both',
      'cloth',
      'decathlon',
      'month',
      'moth',
      'mouth',
      'myth',
      'north',
      'path',
      'pith',
      'sixteenth',
      'smith',
      'sympathy',
      'teeth',
      'thank',
      'thankless',
      'thatch',
      'theft',
      'thick',
      'thin',
      'thing',
      'things',
      'think',
      'third',
      'thirst',
      'thirsty',
      'thirteen',
      'thorn',
      'threw',
      'throbbing',
      'thrust',
      'thud',
      'thug',
      'thump',
      'tooth',
    ],
    th_02: [
      'brother',
      'father',
      'lather',
      'mother',
      'than',
      'that',
      'the',
      'them',
      'theme',
      'then',
      'therein',
      'these',
      'they',
      'this',
      'thus',
      'rather',
      'with',
      'wither',
      'within',
      'without',
      'withstand',
    ],
  },
};

export const BOLD_VARIATIONS = ['th_01', 'oo_02'];

export const resolvePhoneme = (grapheme: string, word?: string): string => {
  if (!word) {
    return grapheme; // No word context, return grapheme as is
  }

  const wordLower = word.replace('.', '').replace('?', '').trim().toLowerCase();
  const overrides = graphemePhonemeOverrideMap[grapheme];

  if (!overrides) {
    return grapheme;
  }

  for (const phonemeKey in overrides) {
    if (overrides[phonemeKey].includes(wordLower)) {
      return phonemeKey;
    }
  }

  return grapheme; // fallback
};

export const isBoldPhoneme = (grapheme: string, word?: string): boolean => {
  const phoneme = resolvePhoneme(grapheme, word);

  return BOLD_VARIATIONS.includes(phoneme);
};
